import { isServer } from '@mr-yum/frontend-core/dist/support/env'
import { ErrorNotification, toaster } from '@mr-yum/frontend-ui'
import { CartLayout } from 'components/Cart/CartLayout'
import { CartLayoutBody } from 'components/Cart/shared'
import { NotFound } from 'components/Common/NotFound'
import { getMembershipUrl } from 'components/LoyaltyMembership/utils'
import { useLoyaltyMembershipQuery, UserPartsFragment } from 'lib/gql'
import { routes } from 'lib/routes'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { LoyaltyMembershipLoader } from '../LoyaltyMembership/LoyaltyMembershipLoader'
import { LoyaltyMembershipForm } from './LoyaltyMembershipForm'

interface Props {
  user: UserPartsFragment
  venueSlug: string
}

export const LoyaltyMembershipPage = ({ user, venueSlug }: Props) => {
  const intl = useIntl()
  const navProps = { backRoute: routes.venue, pathData: { venueSlug } }

  const [{ data, fetching, error }] = useLoyaltyMembershipQuery({
    variables: { venueSlug },
    pause: isServer,
    requestPolicy: 'network-only',
  })

  const [newlySignedUp, setNewlySignedUp] = useState(false)

  const venue = data?.guestVenue
  const loyaltyDetails = data?.loyaltyDetails
  const metadata = loyaltyDetails?.metadata
  const hasGuestMembership =
    data?.getGuestMembership?.id &&
    user?.email &&
    user?.name &&
    loyaltyDetails?.guestData?.isOptedIn

  const title =
    metadata?.memberName ||
    intl.formatMessage({ defaultMessage: 'Membership signup', id: 'v0Sa/Q' })

  useEffect(() => {
    if (hasGuestMembership || newlySignedUp) {
      const membershipUrl = getMembershipUrl({
        infoLink: metadata?.infoLink,
        guestId: user?.id,
        venueId: venue?.venueId,
        venueSlug,
      })

      if (membershipUrl) {
        window.location.replace(membershipUrl.href)
      } else {
        toaster.negative(
          <div data-testid="loyalty-update-error">
            <FormattedMessage
              defaultMessage="There was an error redirecting to a new page"
              id="0S/c+B"
            />
            ,
          </div>,
          5000,
        )
      }

      return
    }
  }, [
    hasGuestMembership,
    metadata?.infoLink,
    newlySignedUp,
    user?.id,
    venue?.venueId,
    venueSlug,
  ])

  if (error) {
    return (
      <CartLayout title="Error" navBarProps={navProps}>
        <CartLayoutBody>
          <ErrorNotification fullWidth error={error} />
        </CartLayoutBody>
      </CartLayout>
    )
  }

  if (fetching) {
    return <LoyaltyMembershipLoader />
  }

  if (!metadata?.infoLink || !venue?.organizationId) {
    return <NotFound message="Loyalty membership is not enabled" />
  }

  // Handle flash before useEffect is navigating
  if (hasGuestMembership) {
    return <LoyaltyMembershipLoader />
  }

  return (
    <>
      <CartLayout title={title} navBarProps={navProps}>
        <div data-testid="loyalty-content">
          <LoyaltyMembershipForm
            email={user?.email}
            name={user?.name}
            header={metadata.introHeader}
            body={metadata.introBody}
            venueSlug={venueSlug}
            organizationId={venue.organizationId}
            onSuccess={() => setNewlySignedUp(true)}
          />
        </div>
      </CartLayout>
    </>
  )
}
